import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from '../../utils/formatter';
import { HiChevronRight, HiArrowLeft } from 'react-icons/hi2';
import { Button } from '../Button';

interface BreadcrumbItem {
  label: string;
  href?: string;
  isLast?: boolean;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
  showBackButton?: boolean;
}

export const Breadcrumb = ({ items, className, showBackButton = true }: BreadcrumbProps) => {
  const navigate = useNavigate();

  if (!items || items.length === 0) return null;

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <nav aria-label="Breadcrumb" className={cn("flex items-center space-x-3 text-sm", className)}>
      {/* Botão de voltar */}
      {showBackButton && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="flex items-center space-x-1 px-2 py-1 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <HiArrowLeft className="h-4 w-4" />
            <span>Voltar</span>
          </Button>
          
          {/* Separador */}
          <div className="h-4 w-px bg-gray-300 dark:bg-neutral-600"></div>
        </>
      )}

      {/* Breadcrumbs */}
      <div className="flex items-center space-x-1">
        {items.map((item, index) => (
          <div key={index} className="flex items-center space-x-1">
            {index > 0 && (
              <HiChevronRight className="h-4 w-4 text-gray-400 dark:text-neutral-500" />
            )}
            
            {item.href && !item.isLast ? (
              <NavLink
                to={item.href}
                className="text-gray-600 dark:text-neutral-400 hover:text-gray-900 dark:hover:text-neutral-200 transition-colors duration-200 font-medium px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-neutral-800"
              >
                {item.label}
              </NavLink>
            ) : (
              <span 
                className={cn(
                  "font-medium px-2 py-1",
                  item.isLast 
                    ? "text-gray-900 dark:text-neutral-200" 
                    : "text-gray-600 dark:text-neutral-400"
                )}
              >
                {item.label}
              </span>
            )}
          </div>
        ))}
      </div>
    </nav>
  );
};

// Componentes adicionais para compatibilidade com shadcn/ui
export const BreadcrumbList = ({ children, className, ...props }: React.HTMLAttributes<HTMLOListElement>) => (
  <ol className={cn("flex items-center space-x-1", className)} {...props}>
    {children}
  </ol>
);

export const BreadcrumbItem = ({ children, className, ...props }: React.HTMLAttributes<HTMLLIElement>) => (
  <li className={cn("flex items-center", className)} {...props}>
    {children}
  </li>
);

export const BreadcrumbLink = ({ children, href, className }: { 
  children: React.ReactNode; 
  href: string;
  className?: string;
}) => (
  <NavLink 
    to={href} 
    className={cn(
      "text-gray-600 dark:text-neutral-400 hover:text-gray-900 dark:hover:text-neutral-200 transition-colors duration-200 font-medium",
      className
    )}
  >
    {children}
  </NavLink>
);

export const BreadcrumbPage = ({ children, className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span 
    className={cn("text-gray-900 dark:text-neutral-200 font-medium", className)} 
    {...props}
  >
    {children}
  </span>
);

export const BreadcrumbSeparator = ({ children, className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn("text-gray-400 dark:text-neutral-500", className)} {...props}>
    {children || <HiChevronRight className="h-4 w-4" />}
  </span>
);

export default Breadcrumb;
